import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch } from '../services/store';
import { WebInfoStateType } from 'src/shared/types/src';
import { updateAppInfo } from 'src/shared/redux-state/src';
import { AppRoutes } from '../navigation/routes';
import { getRedirectionRoute } from '../utilities/helpers';
import { URL_CONSTANTS } from '../config/appConstants';
import { LoginPersistActions } from 'src/shared/redux-state/src/lib/login/LoginSlice';

interface UseAppInitializerProps {
  deviceId?: string;
  previousRoute?: string | null;
  emailSetter?: (email: string) => void;
  setValidatingTokens: (value: boolean) => void;
  store: any;
}

export const useAppInitializer = ({
  deviceId,
  previousRoute,
  emailSetter,
  setValidatingTokens,
  store,
}: UseAppInitializerProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const setAppInfo = useCallback(async () => {
    if (!deviceId) return;
    try {
      const appInfo: WebInfoStateType = { uniqueDeviceId: deviceId };
      await dispatch(updateAppInfo({ payload: appInfo }));
    } catch (error) {
      console.log("Error setting app info:", error);
    }
  }, [deviceId, dispatch]);

  const performInitOperations = useCallback(async () => {
    const loginState = (store.getState() as RootState).persistLogin.loginData;

    if (previousRoute?.includes('deep-link')) {
      const token = previousRoute.split('/')[2];
      const payload = JSON.parse(atob(token));
      if (payload?.eventType === 'ACCOUNT_ACTIVATION') {
        emailSetter?.(payload?.email || '');
        navigate(AppRoutes.MOBILE_APP_LANDSCAPE_VIEW);
        return;
      }
    }

    const redirectionRoute = getRedirectionRoute(previousRoute);
    const redirectTo = `${AppRoutes.VERIFY_EMAIL}${redirectionRoute ? `?${URL_CONSTANTS.REDIRECT_TO}=${redirectionRoute}` : ''}`;

    const shouldRedirect = !loginState ||
      !loginState.accessToken ||
      !loginState.refreshToken ||
      !loginState.children?.length;

    if (shouldRedirect) {
      setValidatingTokens(false);
      await dispatch(LoginPersistActions.resetPersistLoginState());
      navigate(redirectTo);
    } else {
      setValidatingTokens(false);
    }
  }, [previousRoute, dispatch, emailSetter, navigate, setValidatingTokens, store]);


  const initializeApp = useCallback(async () => {
    await setAppInfo();
    // check for user session validity
    // if localStorage has loggedIn true, then check if tokens are present and hit the auth api to validate tokens
    // if validation passes, then only expose the routes, else logout, clear the storage and redirect to VERIFY EMAIL
    await performInitOperations();
  }, [setAppInfo, performInitOperations]);

  return { initializeApp };
};
